import * as THREE from 'three';

export interface MatOptions {
  rough?: number;
  metal?: number;
  emissive?: string;
  emissiveIntensity?: number;
  flat?: boolean;
  transparent?: boolean;
  opacity?: number;
  side?: THREE.Side;
  alphaTest?: number;
}

const colorCache = new Map<string, THREE.MeshStandardMaterial>();

/** Shared solid-color material. Sharing is what lets the static batcher merge meshes. */
export function mat(color: string, opts: MatOptions = {}): THREE.MeshStandardMaterial {
  const key = `${color}|${JSON.stringify(opts)}`;
  let m = colorCache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      color,
      roughness: opts.rough ?? 0.85,
      metalness: opts.metal ?? 0,
      flatShading: opts.flat ?? false,
      transparent: opts.transparent ?? false,
      opacity: opts.opacity ?? 1,
      side: opts.side ?? THREE.FrontSide,
      alphaTest: opts.alphaTest ?? 0,
    });
    if (opts.emissive) {
      m.emissive = new THREE.Color(opts.emissive);
      m.emissiveIntensity = opts.emissiveIntensity ?? 1;
    }
    colorCache.set(key, m);
  }
  return m;
}

const texCache = new Map<string, THREE.MeshStandardMaterial>();

export function texMat(
  map: THREE.Texture,
  opts: MatOptions & { color?: string } = {},
): THREE.MeshStandardMaterial {
  const key = `${map.uuid}|${JSON.stringify(opts)}`;
  let m = texCache.get(key);
  if (!m) {
    m = new THREE.MeshStandardMaterial({
      map,
      color: opts.color ?? '#ffffff',
      roughness: opts.rough ?? 0.85,
      metalness: opts.metal ?? 0,
      transparent: opts.transparent ?? false,
      opacity: opts.opacity ?? 1,
      side: opts.side ?? THREE.FrontSide,
      alphaTest: opts.alphaTest ?? 0,
      flatShading: opts.flat ?? false,
    });
    if (opts.emissive) {
      m.emissive = new THREE.Color(opts.emissive);
      m.emissiveIntensity = opts.emissiveIntensity ?? 1;
      m.emissiveMap = map;
    }
    texCache.set(key, m);
  }
  return m;
}

/**
 * Materials that light up at night (windows, bulbs, neon). The sky system scales
 * their emissive intensity by darkness each frame.
 */
const nightGlow = new Map<THREE.MeshStandardMaterial, number>();

export function registerNightGlow(
  m: THREE.MeshStandardMaterial,
  maxIntensity: number,
): THREE.MeshStandardMaterial {
  nightGlow.set(m, maxIntensity);
  return m;
}

export function updateNightGlow(darkness: number): void {
  for (const [m, max] of nightGlow) m.emissiveIntensity = max * darkness;
}

/**
 * Box geometry whose UVs are scaled to world units, so a repeating texture tiles at
 * the same density on every face regardless of the box's proportions.
 */
export function worldBox(w: number, h: number, d: number, tile = 1): THREE.BoxGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  const uv = g.attributes.uv as THREE.BufferAttribute;
  // Face order: +x, -x, +y, -y, +z, -z (4 vertices each).
  const dims: Array<[number, number]> = [
    [d, h],
    [d, h],
    [w, d],
    [w, d],
    [w, h],
    [w, h],
  ];
  for (let f = 0; f < 6; f++) {
    const [du, dv] = dims[f] as [number, number];
    for (let v = 0; v < 4; v++) {
      const i = f * 4 + v;
      uv.setXY(i, (uv.getX(i) * du) / tile, (uv.getY(i) * dv) / tile);
    }
  }
  uv.needsUpdate = true;
  return g;
}

/** Convenience: build a mesh, position it, and flag it as a shadow caster. */
export function mesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  opts: { cast?: boolean; receive?: boolean } = {},
): THREE.Mesh {
  const m = new THREE.Mesh(geometry, material);
  m.position.set(x, y, z);
  m.castShadow = opts.cast ?? true;
  m.receiveShadow = opts.receive ?? true;
  return m;
}

export function box(
  w: number,
  h: number,
  d: number,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  opts?: { cast?: boolean; receive?: boolean },
): THREE.Mesh {
  return mesh(new THREE.BoxGeometry(w, h, d), material, x, y, z, opts);
}

export function cyl(
  rTop: number,
  rBottom: number,
  h: number,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  segments = 12,
  opts?: { cast?: boolean; receive?: boolean; open?: boolean },
): THREE.Mesh {
  return mesh(
    new THREE.CylinderGeometry(rTop, rBottom, h, segments, 1, opts?.open ?? false),
    material,
    x,
    y,
    z,
    opts,
  );
}

export function sphere(
  r: number,
  material: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
  segments = 12,
  opts?: { cast?: boolean; receive?: boolean },
): THREE.Mesh {
  return mesh(
    new THREE.SphereGeometry(r, segments, Math.max(6, Math.floor(segments * 0.75))),
    material,
    x,
    y,
    z,
    opts,
  );
}

/** Frequently reused materials. */
export const M = {
  get cinder() {
    return mat('#8d8a84', { rough: 0.95 });
  },
  get black() {
    return mat('#1d1d1f', { rough: 0.6 });
  },
  get rubber() {
    return mat('#1a1a1a', { rough: 0.9 });
  },
  get chrome() {
    return mat('#d5d8dc', { rough: 0.25, metal: 0.8 });
  },
  get steel() {
    return mat('#8f969c', { rough: 0.45, metal: 0.6 });
  },
  get white() {
    return mat('#f2f0ea');
  },
  get darkGlass() {
    return mat('#1c2a33', { rough: 0.15, metal: 0.4 });
  },
  get pole() {
    return mat('#6b5137', { rough: 0.95 });
  },
  get trunk() {
    return mat('#5a4030', { rough: 0.95 });
  },
};
